/**
 * panes.js — the three-pane layout, on Split.js.
 *
 * Two splits share one row: the tree against everything right of it, and the
 * editor against the preview. They are separate instances because the tree
 * collapses and the pair does not, and because below the breakpoint the pair
 * turns vertical while the tree leaves the layout altogether.
 *
 * Sizes persist. A pane you sized is a pane you meant to size, and losing it on
 * reload makes the handle feel like it did nothing.
 */

import Split from 'split.js';

const body = document.body;

// A seeded workspace is laid out in a sidepanel more often than a tab, so it keeps its own.
const KEY = new URLSearchParams(location.search).has('workspace') ? 'rvmark-playground:seeded:' : 'rvmark-playground:';
const MAIN_W  = KEY + 'main-w';   // [tree%, rest%], horizontal
const MAIN_H  = KEY + 'main-h';   // [tree%, rest%], vertical
const PAIR_W  = KEY + 'pair-w';   // [editor%, preview%], horizontal
const PAIR_H  = KEY + 'pair-h';   // [editor%, preview%], vertical
const COLLAPSED = KEY + 'tree-collapsed';
const PREVIEW_HIDDEN = KEY + 'preview-hidden';

// The handle paints a 1px rule; the rest of its width is padding it steals
// back with a negative margin, so this is what the panes actually give up.
const GUTTER = 1;
const NARROW = window.matchMedia('(max-width: 800px)');

/** Sizes are two percentages that must still sum to 100 after a schema change,
 *  so a stored pair that fails to parse falls back rather than half-applying. */
function readSizes(key, fallback) {
  try {
    const v = JSON.parse(localStorage.getItem(key) || '');
    if (Array.isArray(v) && v.length === 2 && v.every(n => typeof n === 'number' && n > 0)) {
      return v;
    }
  } catch (e) {}
  return fallback;
}

const save = (key, sizes) => localStorage.setItem(key, JSON.stringify(sizes));

// Split.js writes calc() widths onto the elements it manages. The gutter it
// makes is a bare div, so the class is ours to name and style.
function gutter(index, direction) {
  const el = document.createElement('div');
  el.className = 'gutter gutter-' + direction;
  return el;
}

// body.dragging kills selection and shields the iframe; the lit state belongs
// to the one gutter being held, or grabbing either would light both.
function dragging(on, gut) {
  body.classList.toggle('dragging', on);
  if (gut) gut.classList.toggle('active', on);
}

/** Split.js hands the gutter to the callbacks only via the instance, so each
 *  split keeps the element its own factory made. */
function gutterOf(store) {
  return (index, direction) => (store.el = gutter(index, direction));
}

// ── The pair: editor against preview ─────────────────────────────────────────
// It is rebuilt on crossing the breakpoint, because direction is fixed at
// construction and the two orientations remember their sizes separately.

const pairGutter = { el: null };
const mainGutter = { el: null };

let pair = null;

/** destroy() leaves the inline calc() the old orientation wrote, which the new
 *  one would then be sized against. Both dimensions go, and the gutter with
 *  them — Split.js builds a fresh one. */
function clear(sel) {
  const el = document.querySelector(sel);
  if (el) { el.style.width = ''; el.style.height = ''; }
}

function buildPair() {
  if (pair) {
    pair.destroy();
    clear('#editor-pane');
    clear('#frame');
    pair = null;
  }
  const vertical = NARROW.matches;
  const key = vertical ? PAIR_H : PAIR_W;
  pair = Split(['#editor-pane', '#frame'], {
    sizes: readSizes(key, [50, 50]),
    minSize: vertical ? 80 : 200,
    gutterSize: GUTTER,
    direction: vertical ? 'vertical' : 'horizontal',
    cursor: vertical ? 'row-resize' : 'col-resize',
    gutter: gutterOf(pairGutter),
    onDragStart: () => dragging(true, pairGutter.el),
    onDragEnd: sizes => { dragging(false, pairGutter.el); save(key, sizes); },
  });
  if (localStorage.getItem(PREVIEW_HIDDEN) === '1') hidePreview(true);
}

/** Done in CSS rather than setSizes, which never trims the preview below its
 *  minSize; Split.js's inline sizes stay put for when it comes back. */
export function hidePreview(on) {
  body.classList.toggle('preview-hidden', on);
  localStorage.setItem(PREVIEW_HIDDEN, on ? '1' : '0');
}

export function togglePreview() {
  hidePreview(!body.classList.contains('preview-hidden'));
}

// ── The tree: collapsible sidebar ────────────────────────────────────────────
// snapOffset turns a drag toward the left edge into a collapse, which is the
// gesture a sidebar is expected to answer to. Split.js collapses to minSize 0
// on its own; the class is what stops the pane painting a border at nothing.

let main = null;

/** The tree's key and default differ by orientation: stacked, a file list
 *  wants a strip off the top, not a fifth of the screen. */
function mainKey() { return NARROW.matches ? MAIN_H : MAIN_W; }
function mainDefault() { return NARROW.matches ? [22, 78] : [18, 82]; }

function buildMain() {
  if (main) { main.destroy(); main = null; }
  clear('#tree-pane');
  clear('#main-pane');
  const vertical = NARROW.matches;
  const key = mainKey();
  main = Split(['#tree-pane', '#main-pane'], {
    sizes: readSizes(key, mainDefault()),
    minSize: vertical ? [0, 160] : [0, 320],
    snapOffset: 60,
    gutterSize: GUTTER,
    direction: vertical ? 'vertical' : 'horizontal',
    cursor: vertical ? 'row-resize' : 'col-resize',
    gutter: gutterOf(mainGutter),
    onDrag: sizes => body.classList.toggle('tree-collapsed', sizes[0] < 1),
    onDragStart: () => dragging(true, mainGutter.el),
    onDragEnd: sizes => {
      dragging(false, mainGutter.el);
      const shut = sizes[0] < 1;
      body.classList.toggle('tree-collapsed', shut);
      localStorage.setItem(COLLAPSED, shut ? '1' : '0');
      // A collapsed size is not a size worth restoring to — keep the last real
      // one, so the toggle has somewhere to open back to.
      if (!shut) save(key, sizes);
    },
  });
  if (localStorage.getItem(COLLAPSED) === '1') collapse(true);
}

function collapse(on) {
  body.classList.toggle('tree-collapsed', on);
  localStorage.setItem(COLLAPSED, on ? '1' : '0');
  if (!main) return;
  main.setSizes(on ? [0, 100] : readSizes(mainKey(), mainDefault()));
}

document.getElementById('tree-toggle').addEventListener('click', () => {
  collapse(!body.classList.contains('tree-collapsed'));
});

// Crossing the breakpoint restyles the panes under Split.js's feet, so both
// instances are rebuilt rather than reconfigured.
NARROW.addEventListener('change', () => { buildMain(); buildPair(); });

buildMain();
buildPair();
